import { useState, useEffect, useCallback } from 'react';
import { buildApiUrl, freshFetchInit } from '../utils/apiBase.js';

/**
 * Stripe 上の支払い方法登録有無（/api/clinic/payment-info-status）
 * Billing から戻った直後などに window focus で再取得する。
 *
 * このエンドポイントは doctor/admin 限定（サーバー側 requireDoctorOrAdmin）。
 * 権限の無いロールでは 403 が確定するため、enabled=false のときは
 * リクエストを一切投げず、状態も null（=未取得）に固定する。
 *
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] doctor/admin のときのみ true を渡す
 */
export function useClinicPaymentMethodStatus({ enabled = true } = {}) {
  const [hasPaymentMethod, setHasPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(enabled);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setHasPaymentMethod(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/clinic/payment-info-status'), freshFetchInit({
        method: 'GET',
      }));
      const data = await res.json().catch(() => ({}));
      setHasPaymentMethod(res.ok && data.hasPaymentMethod === true);
    } catch {
      setHasPaymentMethod(false);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setHasPaymentMethod(null);
      setLoading(false);
      return undefined;
    }
    refresh();
    const onFocus = () => {
      refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, refresh]);

  return { hasPaymentMethod, loading, refresh };
}
